/**
 * Configuration for Information Sources Visibility
 * 
 * Controls which information sources are visible in the application.
 * Set 'visible: true' to show the source, 'visible: false' to hide it.
 * 
 * Context Categories:
 * - 'shared': Common organizational knowledge shared across all users
 * - 'private': Personal user data, isolated per user identity
 */

export const sourcesConfig = {
  // Internal RAG-powered knowledge base (SHARED)
  internalKnowledgeBase: {
    id: 'internal_kb',
    name: 'Internal Knowledge Base',
    icon: 'fa-book',
    description: 'RAG-powered internal documentation',
    visible: true, // ✅ Visible by default
    type: 'knowledge_base',
    category: 'shared' // Common organizational knowledge
  },

  // Amazon Bedrock Knowledge Base (SHARED)
  bedrockKnowledgeBase: {
    id: 'bedrock_kb',
    name: 'Amazon Bedrock Knowledge Base',
    icon: 'fa-aws',
    description: 'AWS-powered knowledge base',
    visible: false, // ❌ Hidden by default
    type: 'knowledge_base',
    category: 'shared' // Common organizational knowledge
  },

  // Clabs Source Store (SHARED)
  clabsSource: {
    id: 'clabs_source',
    name: 'Clabs Source',
    icon: 'fa-brain',
    description: 'Consilient Labs Source Store',
    visible: true, // ✅ Visible by default
    type: 'clabs',
    category: 'shared' // Common organizational knowledge
  },

  // Customer Database (SHARED)
  customerDatabase: {
    id: 'customer_db',
    name: 'Customer Database',
    icon: 'fa-database',
    description: 'Customer information and transactions',
    visible: false, // ❌ Hidden by default
    type: 'database',
    category: 'shared' // Common organizational data
  },

  // Web Search (SearXNG)
  webSearch: {
    id: 'web_search',
    name: 'Web Search',
    icon: 'fas fa-globe',
    description: 'Search the internet via SearXNG',
    visible: true, // ✅ Visible by default
    type: 'web_scraper',
    category: 'external'
  },

  // Personal Context (PRIVATE)
  personalContext: {
    id: 'personal_context',
    name: 'Personal Context',
    icon: 'fa-user-lock',
    description: 'Use your imported conversation history from ChatGPT, Claude, or other AI assistants',
    visible: true, // ✅ Visible by default
    type: 'personal',
    category: 'private' // Personal user data, not shared
  }
};

/**
 * Get all visible sources
 * @returns {Array} Array of visible source configurations
 */
export const getVisibleSources = () => {
  return Object.values(sourcesConfig).filter(source => source.visible);
};

/**
 * Check if a specific source is visible
 * @param {string} sourceId - The ID of the source to check
 * @returns {boolean} True if the source is visible
 */
export const isSourceVisible = (sourceId) => {
  const source = Object.values(sourcesConfig).find(s => s.id === sourceId);
  return source ? source.visible : false;
};

/**
 * Get source configuration by ID
 * @param {string} sourceId - The ID of the source
 * @returns {Object|null} Source configuration or null if not found
 */
export const getSourceConfig = (sourceId) => {
  return Object.values(sourcesConfig).find(s => s.id === sourceId) || null;
};

/**
 * Get sources by type
 * @param {string} type - The type of sources to get ('knowledge_base', 'clabs', 'database')
 * @returns {Array} Array of source configurations matching the type
 */
export const getSourcesByType = (type) => {
  return Object.values(sourcesConfig).filter(s => s.type === type && s.visible);
};

/**
 * Get sources by category
 * @param {string} category - The category of sources to get ('shared' or 'private')
 * @returns {Array} Array of source configurations matching the category
 */
export const getSourcesByCategory = (category) => {
  return Object.values(sourcesConfig).filter(s => s.category === category && s.visible);
};

export default sourcesConfig;

